import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  RefreshCw, Sparkles, ExternalLink, LogOut, Clock, CheckCircle2,
  FileText, CalendarClock, BarChart3, Mail, Settings as SettingsIcon,
  Rocket, Download, AlertCircle, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import InvestorAdmin from './InvestorAdmin';
import {
  listDrafts, listPublished, startGeneration, getJob, getSchedule,
  getUpcomingTopics, getAnalytics, listNewsletterSignups, newsletterCsvUrl,
  getSettings, updateSettings, manualRedeploy, getToken, clearToken,
} from './api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const formatDay = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short',
    });
  } catch { return iso; }
};

const AdminDashboard = ({ onSignOut }) => {
  const [drafts, setDrafts] = useState([]);
  const [published, setPublished] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [signups, setSignups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [hookInput, setHookInput] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [redeploying, setRedeploying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, p, s, u, an, ns, st] = await Promise.all([
        listDrafts(),
        listPublished(),
        getSchedule().catch(() => null),
        getUpcomingTopics(7).catch(() => null),
        getAnalytics().catch(() => null),
        listNewsletterSignups().catch(() => []),
        getSettings().catch(() => null),
      ]);
      setDrafts(d);
      setPublished(p);
      setSchedule(s);
      setUpcoming(u);
      setAnalytics(an);
      setSignups(ns);
      setSettings(st);
    } catch (err) {
      setError(err.message);
      if (err.message.toLowerCase().includes('session')) onSignOut();
    } finally {
      setLoading(false);
    }
  }, [onSignOut]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { job_id: jobId } = await startGeneration();
      toast.info('Drafting article — takes 30–90 seconds…', {
        description: 'Claude Sonnet 4.5 is writing. The dashboard will update automatically.',
      });
      const deadline = Date.now() + 3 * 60 * 1000;
      while (true) {
        if (Date.now() > deadline) {
          throw new Error('Generation timed out after 3 minutes. Refresh to check for the draft.');
        }
        await new Promise((r) => setTimeout(r, 4000));
        const job = await getJob(jobId);
        if (job.status === 'done') {
          toast.success('Draft ready', { description: 'Scroll to Pending review to open it.' });
          break;
        }
        if (job.status === 'failed') {
          throw new Error(job.error || 'Generation failed');
        }
      }
      await load();
    } catch (err) {
      toast.error('Generation failed', { description: err.message });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveHook = async () => {
    setSavingSettings(true);
    try {
      const next = await updateSettings(hookInput.trim());
      setSettings(next);
      setHookInput('');
      toast.success(next.deploy_hook_configured ? 'Deploy hook saved' : 'Deploy hook cleared');
    } catch (err) {
      toast.error('Could not save', { description: err.message });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRedeploy = async () => {
    setRedeploying(true);
    try {
      const res = await manualRedeploy();
      if (res.ok) toast.success('Render deploy triggered', { description: 'Site rebuild starting on Render.' });
      else toast.error('Deploy failed', { description: `Render returned status ${res.status}` });
    } catch (err) {
      toast.error('Deploy failed', { description: err.message });
    } finally {
      setRedeploying(false);
    }
  };

  const handleSignOut = () => {
    clearToken();
    onSignOut();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 sticky top-0 bg-slate-950/85 backdrop-blur z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Keystone</div>
            <div className="text-base font-medium">Article admin</div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-slate-400 hover:text-white px-3 py-1.5 rounded-md hover:bg-slate-900 transition-colors" data-testid="admin-back-to-site">Back to site</Link>
            <button onClick={load} className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white px-3 py-1.5 rounded-md hover:bg-slate-900 transition-colors" data-testid="admin-refresh-btn">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={handleSignOut} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-red-300 px-3 py-1.5 rounded-md hover:bg-slate-900 transition-colors" data-testid="admin-signout-btn">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Generate + auto-publish status */}
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-md px-5 py-2.5 text-sm font-medium transition-colors"
            data-testid="admin-generate-btn"
          >
            <Sparkles className="w-4 h-4" />
            {generating ? 'Generating (30–60 sec)…' : 'Generate new draft'}
          </button>
          <span className="text-xs text-slate-500">One click, one draft. No GitHub, no waiting.</span>
          {schedule?.next_run && (
            <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-slate-400 border border-slate-800 rounded-full px-3 py-1.5" data-testid="admin-next-run">
              <CalendarClock className="w-3.5 h-3.5" />
              Next auto-publish: {new Date(schedule.next_run).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' })} London
            </span>
          )}
        </div>

        {error && (
          <div className="mb-6 border border-red-900/50 bg-red-950/40 rounded-lg p-4 text-sm text-red-200" data-testid="admin-error">
            {error}
          </div>
        )}

        {/* Content calendar */}
        <section className="mb-12" data-testid="admin-content-calendar">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-blue-400" />
              Content calendar
            </h2>
            <span className="text-xs text-slate-500">
              {upcoming?.remaining_topics ?? '—'} topics remaining in queue
            </span>
          </div>
          <div className="border border-slate-800 rounded-lg divide-y divide-slate-900">
            {(upcoming?.upcoming || []).slice(0, 7).map((t, idx) => {
              // Predict the publish date: next_run is day 1, then +1 day per topic.
              const base = upcoming?.next_run ? new Date(upcoming.next_run) : null;
              const when = base ? new Date(base.getTime() + idx * 24 * 60 * 60 * 1000) : null;
              return (
                <div key={t.slug} className="flex items-center gap-4 px-4 py-3" data-testid={`calendar-row-${t.slug}`}>
                  <div className="text-xs text-slate-500 font-mono w-14 flex-shrink-0">
                    {when ? formatDay(when.toISOString()) : `+${idx}d`}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-blue-400/80 w-24 flex-shrink-0">
                    {t.category}
                  </div>
                  <div className="text-sm text-slate-200 truncate">{t.working_title}</div>
                </div>
              );
            })}
            {(!upcoming || (upcoming.upcoming || []).length === 0) && (
              <div className="px-4 py-8 text-center text-sm text-slate-500">
                No upcoming topics — add more to the queue.
              </div>
            )}
          </div>
        </section>

        {/* Pending drafts */}
        <section className="mb-12">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-medium">Pending review</h2>
            <span className="text-xs text-slate-500">
              {loading ? 'loading…' : `${drafts.length} draft${drafts.length === 1 ? '' : 's'}`}
            </span>
          </div>
          {!loading && drafts.length === 0 && (
            <div className="border border-dashed border-slate-800 rounded-lg p-10 text-center text-slate-400" data-testid="admin-no-drafts">
              <FileText className="w-6 h-6 mx-auto mb-3 opacity-60" />
              <p className="text-sm">No drafts. Click <em>Generate new draft</em> above to create one.</p>
            </div>
          )}
          <ul className="space-y-3">
            {drafts.map((a) => (
              <li key={a.id}>
                <Link
                  to={`/admin/review/${a.id}`}
                  className="block border border-slate-800 hover:border-blue-600 rounded-lg p-5 transition-colors group"
                  data-testid={`admin-draft-${a.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-1">
                        {a.topic_category || 'draft'} · <code className="text-slate-400">{a.slug}</code>
                      </div>
                      <div className="text-base font-medium text-white group-hover:text-blue-300 transition-colors truncate">
                        {a.title}
                      </div>
                      <div className="mt-2 text-xs text-slate-500 flex items-center gap-3">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(a.created_at)}</span>
                      </div>
                    </div>
                    <div className="text-xs text-blue-400 flex-shrink-0 whitespace-nowrap">Review →</div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {/* Analytics — published articles with view counts */}
        <section className="mb-12" data-testid="admin-analytics">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-blue-400" />
              Article analytics
            </h2>
            <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" />
              {analytics?.total_views ?? 0} total views · {published.length} published
            </span>
          </div>
          <ul className="border border-slate-800 rounded-lg divide-y divide-slate-900">
            {(analytics?.articles || published.map((p) => ({ ...p, views: 0 }))).map((a) => (
              <li key={a.slug} className="flex items-center justify-between gap-4 px-4 py-3" data-testid={`analytics-row-${a.slug}`}>
                <div className="min-w-0 flex items-center gap-3">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-slate-300 truncate">{a.title}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
                  <span className="font-mono text-slate-300" data-testid={`analytics-views-${a.slug}`}>
                    {a.views ?? 0} views
                  </span>
                  <span>{formatDate(a.published_at)}</span>
                  <a
                    href={`/resources/${a.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-slate-400 hover:text-blue-300"
                    data-testid={`admin-published-view-${a.slug}`}
                  >
                    View live <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Newsletter signups */}
        <section className="mb-12" data-testid="admin-newsletter">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-400" />
              Newsletter signups
            </h2>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span>{signups.length} subscriber{signups.length === 1 ? '' : 's'}</span>
              {signups.length > 0 && (
                <a
                  href={`${newsletterCsvUrl()}?token=${getToken()}`}
                  onClick={async (e) => {
                    // The admin CSV endpoint needs the Bearer token in a header —
                    // browsers can't send headers on a plain <a> click, so fetch
                    // + blob-download instead of navigating away.
                    e.preventDefault();
                    try {
                      const res = await fetch(newsletterCsvUrl(), { headers: { Authorization: `Bearer ${getToken()}` } });
                      if (!res.ok) throw new Error('Export failed');
                      const blob = await res.blob();
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'keystone-newsletter.csv';
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      URL.revokeObjectURL(url);
                    } catch (err) {
                      toast.error('CSV export failed', { description: err.message });
                    }
                  }}
                  className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"
                  data-testid="admin-newsletter-csv"
                >
                  <Download className="w-3 h-3" /> Export CSV
                </a>
              )}
            </div>
          </div>
          <div className="border border-slate-800 rounded-lg divide-y divide-slate-900 max-h-72 overflow-auto">
            {signups.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-slate-500" data-testid="admin-no-signups">
                No signups yet. The signup form is live at the bottom of every article.
              </div>
            )}
            {signups.map((s) => (
              <div key={s.id || s.email} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm" data-testid={`signup-row-${s.email}`}>
                <span className="text-slate-200 truncate">{s.email}</span>
                <div className="flex items-center gap-4 text-xs text-slate-500 flex-shrink-0">
                  <span className="hidden sm:inline truncate max-w-[16rem]">{s.source || '—'}</span>
                  <span>{formatDate(s.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Site settings — deploy hook */}
        <section className="mb-12" data-testid="admin-settings">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <SettingsIcon className="w-4 h-4 text-blue-400" />
              Site settings
            </h2>
          </div>
          <div className="border border-slate-800 rounded-lg p-5 space-y-4">
            <div>
              <div className="text-sm font-medium text-white mb-1">Render deploy hook</div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">
                Paste the deploy-hook URL from your Render static site (Settings →
                Deploy Hook → Copy URL). After each 7 AM auto-publish, the backend
                calls this URL so Render rebuilds and the new article&rsquo;s deep URL
                is shareable within ~3 minutes.
              </p>
              {settings?.deploy_hook_configured ? (
                <div className="flex items-center gap-2 text-xs text-emerald-400 mb-2" data-testid="settings-hook-status">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Configured: <code className="text-slate-300">{settings.deploy_hook_masked}</code>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-amber-400 mb-2" data-testid="settings-hook-status">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Not configured — new articles won&rsquo;t trigger a rebuild.
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="url"
                  placeholder="https://api.render.com/deploy/srv-…?key=…"
                  value={hookInput}
                  onChange={(e) => setHookInput(e.target.value)}
                  className="flex-1 min-w-0 rounded-md border border-slate-800 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="settings-hook-input"
                />
                <button
                  onClick={handleSaveHook}
                  disabled={savingSettings}
                  className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-md px-4 py-2 text-sm font-medium transition-colors"
                  data-testid="settings-hook-save"
                >
                  {savingSettings ? 'Saving…' : (settings?.deploy_hook_configured ? 'Update' : 'Save')}
                </button>
                <button
                  onClick={handleRedeploy}
                  disabled={redeploying || !settings?.deploy_hook_configured}
                  className="inline-flex items-center gap-1.5 justify-center border border-slate-700 hover:border-blue-500 hover:text-blue-300 disabled:opacity-40 rounded-md px-4 py-2 text-sm text-slate-300 transition-colors"
                  data-testid="settings-redeploy-btn"
                >
                  <Rocket className="w-3.5 h-3.5" />
                  {redeploying ? 'Deploying…' : 'Deploy now'}
                </button>
              </div>
              {settings?.deploy_hook_configured && (
                <p className="text-[11px] text-slate-500 mt-2">
                  Tip: leave the input blank and click <em>Update</em> to clear the hook.
                </p>
              )}
            </div>
          </div>
        </section>

        <InvestorAdmin />
      </main>
    </div>
  );
};

export default AdminDashboard;
